/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import Backbone from 'backbone';
import { View } from 'backbone.marionette';
import Syphon from 'backbone.syphon';
import _ from 'lodash';

import { formatDisplayDate } from '@/util/MomentUtils';
import InputListView from '@/views/InputListView';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import makeFormReadOnly from '@/util/makeFormReadOnly';

import UpdateOrderPackageInformation from '../../services/UpdateOrderPackageInformation';

import tmplOrderPackageInformationForm from './OrderPackageInformationForm.hbs';

const OrderPackageInformationForm = View.extend({
  template: tmplOrderPackageInformationForm,

  className: 'order-package-information-form',

  ui: {
    commandsContainer: '.commands-container',
    trackerNumContainer: '.tracker-number-container',
    popDate: 'input[name="popDate"]',
    customerContactDate: 'input[name="customerContactDate"]',
    submittedOn: 'input[name="submittedOn"]'
  },

  events: {
    'change @ui.popDate': 'onChangePopDate'
  },

  regions: {
    commandsContainer: '@ui.commandsContainer',
    trackerNumContainer: '@ui.trackerNumContainer'
  },

  toggleForm(state) {
    this.$el.find(':input[name=customerContactDate]').prop('disabled', !state);
  },

  templateContext() {
    const popUpdatedBy = this.model.get('orderPackage').popDateUpdatedBy;
    const popUpdatedOn = formatDisplayDate(this.model.get('orderPackage').popDateUpdatedOn);
    return {
      popUpdatedBy,
      popUpdatedOn
    };
  },

  onRender() {
    if (!this.options.editable) {
      this.toggleForm(false);
      makeFormReadOnly(this.$el);
    }

    const orderPackage = this.model.get('orderPackage');

    this.ui.popDate.datepicker({ format: 'mm/dd/yyyy' });
    this.ui.popDate.datepicker('setDate', orderPackage.popDate);

    this.ui.customerContactDate.datepicker({ format: 'mm/dd/yyyy' });
    this.ui.customerContactDate.datepicker('setDate', orderPackage.customerContactDate);

    this.ui.submittedOn.datepicker({ format: 'mm/dd/yyyy' });
    this.ui.submittedOn.datepicker('setDate', orderPackage.submittedOn);

    this.showChildView(
      'commandsContainer',
      new InputListView({
        model: new Backbone.Model({
          label: 'All Commands in Order Package',
          ratio: 'half',
          type: 'text',
          inputName: 'name',
          ariaLabel: 'Command Name',
          isReadOnly: true,
          emptyText: 'No Commands Found'
        }),
        collection: new Backbone.Collection(orderPackage.commands)
      })
    );

    const trackerNumbers = _.filter(orderPackage.trackerNumbers, (num) => {
      return num.name;
    });
    this.showChildView(
      'trackerNumContainer',
      new InputListView({
        model: new Backbone.Model({
          label: 'Tracker #(s)',
          ratio: 'half',
          type: 'text',
          inputName: 'name',
          isReadOnly: true,
          emptyText: 'No Tracker Numbers Found'
        }),
        collection: new Backbone.Collection(trackerNumbers)
      })
    );

    this.model.set('popChanged', false);
  },

  async onSave(e) {
    const formData = Syphon.serialize(this);
    formData.orderPackageID = this.options.orderPackageID;
    this.toggleForm(false);
    if (this.model.get('popChanged')) {
      formData.popChanged = 1;
    }
    try {
      await UpdateOrderPackageInformation(formData);

      showSuccessMessage({
        code: 'saveSuccess',
        title: 'Save Success',
        message: 'Order Package Information Saved.',
        hideAfter: 1
      });
    } catch (err) {
      throw new Error(err);
    } finally {
      this.toggleForm(true);
    }
  },

  onChangePopDate() {
    this.model.set('popChanged', true);
  }
});

export default OrderPackageInformationForm;
